import { toast } from 'react-toastify';
import { invalidateTeams } from '@queries/fetchTeams';
import { createTeam, renameTeam, setTeamStatus } from '@src/mutations/team.mutations';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { TeamStatus } from '../types';

export const useTeamMutations = ({ onSettled }: { onSettled: () => void }) => {
  const queryClient = useQueryClient();
  const refreshTeams = () => invalidateTeams(queryClient);

  const create = useMutation({ mutationFn: createTeam, onSuccess: refreshTeams });
  const rename = useMutation({ mutationFn: renameTeam, onSuccess: refreshTeams });
  const status = useMutation({ mutationFn: setTeamStatus, onSuccess: refreshTeams });

  const onCreate = (name: string) =>
    create.mutate(name, {
      onSuccess: () => {
        toast.success('Team created');
        onSettled();
      },
    });

  const onRename = (team: { id: number; name: string }) =>
    rename.mutate(team, {
      onSuccess: () => {
        toast.success('Team renamed');
        onSettled();
      },
    });

  const onSetStatus = (team: { id: number; status: TeamStatus }) =>
    status.mutate(team, {
      onSuccess: () => {
        toast.success(team.status === 'Active' ? 'Team reactivated' : 'Team disabled');
        onSettled();
      },
    });

  return {
    onCreate,
    onRename,
    onSetStatus,
    isPending: create.isPending || rename.isPending || status.isPending,
  };
};
